import { useState, useEffect, useMemo } from 'react';
import {
  Button,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Table,
  Tag,
  Switch,
  notification,
  Card,
  Row,
  Col,
} from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { t } from '../../../i18n/i18n';
import TreeMultiSelect from '../../TreeMultiSelect/TreeMultiSelect';
import { useLanguageTranslations } from '../../../hooks/useLanguageTranslations';
import useTranslations from '../../../hooks/useTranslations';
import { getTranslatedValue, ensureTranslationObject } from '../../../utils/translationHelpers';
// import './PresetEditor.scss'; // Removed: using utility classes instead

const { Option } = Select;

export const PresetEditor = ({
  preset,
  testDifficulty,
  testQuestions,
  withThemes = false,
  availableSubskills = [],
  getAvailableQuestionsCount: propGetAvailableQuestionsCount,
  onSave,
  onCancel,
}) => {
  const [form] = Form.useForm();
  const { key: langKey } = useTranslations();
  const { languagesList } = useLanguageTranslations();
  const [expandedLanguages, setExpandedLanguages] = useState(false);

  // Calculate available questions count by difficulty/theme/subskills
  const getAvailableQuestionsCount =
    propGetAvailableQuestionsCount ||
    ((difficultyId, theme = null, subSkillIds = []) => {
      if (!testQuestions || testQuestions.length === 0) return 0;

      return testQuestions.filter((q) => {
        const matchDifficulty = q.difficultyId === difficultyId;

        // If subskills are selected, only count questions that have at least one of the selected subskills
        const matchSubskills =
          !subSkillIds ||
          subSkillIds.length === 0 ||
          (q.subSkillIds && q.subSkillIds.some((subskillId) => subSkillIds.includes(subskillId)));

        if (!withThemes) {
          return matchDifficulty && matchSubskills;
        }

        const matchTheme = !theme || q.theme === theme;
        return matchDifficulty && matchTheme && matchSubskills;
      }).length;
    });

  // Initialize difficulty configs with correct maxQuantity
  const initializeDifficultyConfigs = () => {
    // If it's a new preset, ensure we start with empty configs
    if (preset.isNew === true) {
      return [];
    }

    // For existing presets, return the difficultyConfig
    const configs = preset.difficultyConfig || [];
    return configs.map((config) => ({
      ...config,
      maxQuantity: getAvailableQuestionsCount(
        config.difficultyId,
        withThemes ? config.theme : null,
        config.subSkillIds || []
      ),
    }));
  };

  const [difficultyConfigs, setDifficultyConfigs] = useState(initializeDifficultyConfigs());
  const [isMobile, setIsMobile] = useState(false);

  // Get unique themes from test questions
  const availableThemes = useMemo(() => {
    if (!withThemes || !testQuestions) return [];
    const themes = [...new Set(testQuestions.filter((q) => q.theme).map((q) => q.theme))];
    return themes.sort();
  }, [withThemes, testQuestions]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Reset configs when preset changes
  useEffect(() => {
    const newConfigs = initializeDifficultyConfigs();
    setDifficultyConfigs(newConfigs);
    // Reset form with preset values
    const displayNameObj = ensureTranslationObject(preset.displayName);
    const formValues = {
      displayName: preset.isNew ? '' : getTranslatedValue(preset.displayName, langKey) || '',
    };

    // Add translations for other languages
    languagesList.forEach((lang) => {
      if (displayNameObj[lang.value]) {
        formValues[`displayName_${lang.value}`] = displayNameObj[lang.value];
      }
    });

    form.setFieldsValue(formValues);
  }, [preset.id, preset.isNew, form, langKey, languagesList]);

  const handleAddDifficultyConfig = () => {
    const newConfig = {
      difficultyId: '',
      theme: '',
      order: difficultyConfigs.length,
      quantity: 1,
      disabled: false,
      workspaceId: [],
      displayName: '',
      maxQuantity: 0, // Will be updated when difficulty/theme is selected
      duration: 2,
      listeningTest: false,
      subSkillIds: [],
    };
    setDifficultyConfigs([...difficultyConfigs, newConfig]);
  };

  const handleRemoveDifficultyConfig = (index) => {
    const newConfigs = difficultyConfigs.filter((_, idx) => idx !== index);
    // Reorder remaining configs
    const reorderedConfigs = newConfigs.map((config, idx) => ({
      ...config,
      order: idx,
    }));
    setDifficultyConfigs(reorderedConfigs);
  };

  const handleConfigChange = (index, field, value) => {
    const newConfigs = [...difficultyConfigs];
    newConfigs[index] = {
      ...newConfigs[index],
      [field]: value,
    };

    // Auto-populate displayName based on difficulty selection
    if (field === 'difficultyId') {
      const difficulty = testDifficulty.find((d) => d.id === value);
      if (difficulty) {
        newConfigs[index].displayName = t(`CREATE_TEST_GROUP_QUESTION_${difficulty.name}`);
      }
      // Update maxQuantity based on available questions
      const theme = withThemes ? newConfigs[index].theme : null;
      const subSkillIds = newConfigs[index].subSkillIds || [];
      const maxQuantity = getAvailableQuestionsCount(value, theme, subSkillIds);
      newConfigs[index].maxQuantity = maxQuantity;
      // Clamp quantity to not exceed maxQuantity
      newConfigs[index].quantity = Math.min(newConfigs[index].quantity || 1, maxQuantity || 1);
    }

    // Update maxQuantity when theme changes
    if (field === 'theme' && withThemes) {
      const { difficultyId, subSkillIds } = newConfigs[index];
      if (difficultyId) {
        const maxQuantity = getAvailableQuestionsCount(difficultyId, value, subSkillIds || []);
        newConfigs[index].maxQuantity = maxQuantity;
        // Clamp quantity to not exceed maxQuantity
        newConfigs[index].quantity = Math.min(newConfigs[index].quantity || 1, maxQuantity || 1);
      }
    }

    // Update maxQuantity when subskills change
    if (field === 'subSkillIds') {
      const { difficultyId, theme } = newConfigs[index];
      if (difficultyId) {
        const maxQuantity = getAvailableQuestionsCount(
          difficultyId,
          withThemes ? theme : null,
          value || []
        );
        newConfigs[index].maxQuantity = maxQuantity;
        // Clamp quantity to not exceed maxQuantity
        newConfigs[index].quantity = Math.min(newConfigs[index].quantity || 1, maxQuantity || 1);
      }
    }

    setDifficultyConfigs(newConfigs);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      // Validate difficulty configs
      if (difficultyConfigs.length === 0) {
        notification.open({
          message: t('TESTGROUP_ADVANCED_ERROR_NO_CONFIGS'),
          type: 'error',
        });
        return;
      }

      // Validate each config has required fields
      for (let i = 0; i < difficultyConfigs.length; i += 1) {
        const config = difficultyConfigs[i];
        if (!config.difficultyId) {
          notification.open({
            message: t('TESTGROUP_ADVANCED_ERROR_MISSING_DIFFICULTY').replace('#{index}', i + 1),
            type: 'error',
          });
          return;
        }
        if (config.quantity < 1 || config.quantity > config.maxQuantity) {
          notification.open({
            message: t('TESTGROUP_ADVANCED_ERROR_INVALID_QUANTITY').replace('#{index}', i + 1),
            type: 'error',
          });
          return;
        }

        // Validate theme or subskills for test groups with themes
        if (withThemes) {
          const hasTheme = config.theme && config.theme.trim() !== '';
          const hasSubskills = config.subSkillIds && config.subSkillIds.length > 0;
          if (!hasTheme && !hasSubskills) {
            notification.open({
              message: t('THEME_OR_SUBSKILLS_REQUIRED').replace(
                '{{difficulty}}',
                config.displayName ||
                  t(
                    `CREATE_TEST_GROUP_QUESTION_${
                      testDifficulty.find((d) => d.id === config.difficultyId)?.name || 'UNKNOWN'
                    }`
                  )
              ),
              type: 'error',
            });
            return;
          }
        }
      }

      // Build displayName translations object
      const displayNameTranslations = {};

      // Primary language (current language)
      displayNameTranslations[langKey] = values.displayName;

      // Other languages
      languagesList.forEach((lang) => {
        const fieldName = `displayName_${lang.value}`;
        if (values[fieldName]) {
          displayNameTranslations[lang.value] = values[fieldName];
        }
      });

      const updatedPreset = {
        ...preset,
        displayName: displayNameTranslations,
        difficultyConfig: difficultyConfigs,
      };

      onSave(updatedPreset);
    } catch (error) {
      // Error handled by form validation
    }
  };

  // Responsive columns configuration
  const columns = [
    {
      title: '#',
      key: 'index',
      width: 50,
      align: 'center',
      render: (_, __, index) => <span className="font-weight-600">{index + 1}</span>,
    },
    {
      title: t('TESTGROUP_ADVANCED_DIFFICULTY'),
      dataIndex: 'difficultyId',
      key: 'difficultyId',
      width: 150,
      ellipsis: true,
      render: (value, _, index) => (
        <Select
          value={value}
          onChange={(val) => handleConfigChange(index, 'difficultyId', val)}
          style={{ width: '100%' }}
          placeholder={t('TESTGROUP_ADVANCED_SELECT_DIFFICULTY')}
        >
          {testDifficulty
            .sort((a, b) => a.order - b.order)
            .map((difficulty) => (
              <Option key={difficulty.id} value={difficulty.id}>
                {t(`CREATE_TEST_GROUP_QUESTION_${difficulty.name}`)}
              </Option>
            ))}
        </Select>
      ),
    },
    ...(withThemes
      ? [
          {
            title: t('TESTGROUP_ADVANCED_THEME'),
            dataIndex: 'theme',
            key: 'theme',
            responsive: ['lg'],
            width: 150,
            ellipsis: true,
            render: (value, _, index) => (
              <Select
                value={value}
                onChange={(val) => handleConfigChange(index, 'theme', val)}
                style={{ width: '100%' }}
                placeholder={t('TESTGROUP_ADVANCED_SELECT_THEME')}
              >
                {availableThemes.map((theme) => (
                  <Option key={theme} value={theme}>
                    {theme}
                  </Option>
                ))}
              </Select>
            ),
          },
        ]
      : []),
    {
      title: t('TESTGROUP_ADVANCED_QUANTITY'),
      dataIndex: 'quantity',
      key: 'quantity',
      width: 80,
      align: 'center',
      render: (value, record, index) => (
        <InputNumber
          value={value}
          onChange={(val) => handleConfigChange(index, 'quantity', val)}
          min={1}
          max={record.maxQuantity}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: t('TESTGROUP_ADVANCED_AVAILABLE_QUESTIONS'),
      dataIndex: 'maxQuantity',
      key: 'maxQuantity',
      responsive: ['lg'],
      width: 120,
      align: 'center',
      render: (value, record) => (
        <Tag color={value > 0 ? 'green' : 'red'}>
          {value || 0} {t('AVAILABLE')}
        </Tag>
      ),
    },
    {
      title: t('TESTGROUP_ADVANCED_DURATION_MIN'),
      dataIndex: 'duration',
      key: 'duration',
      responsive: ['md'],
      width: 100,
      align: 'center',
      render: (value, _, index) => (
        <InputNumber
          value={value}
          onChange={(val) => handleConfigChange(index, 'duration', val)}
          min={1}
          max={60}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: t('TESTGROUP_ADVANCED_LISTENING'),
      dataIndex: 'listeningTest',
      key: 'listeningTest',
      responsive: ['lg'],
      width: 80,
      align: 'center',
      render: (value, _, index) => (
        <Switch
          checked={value}
          onChange={(checked) => handleConfigChange(index, 'listeningTest', checked)}
        />
      ),
    },
    {
      title: t('SUBSKILLS'),
      dataIndex: 'subSkillIds',
      key: 'subSkillIds',
      width: 300,
      render: (value, _, index) => (
        <TreeMultiSelect
          options={availableSubskills}
          value={value || []}
          onChange={(selectedIds) => handleConfigChange(index, 'subSkillIds', selectedIds)}
          label={t('SELECT_SUBSKILLS')}
          labelName="name"
          width="100%"
          dropdownWidth={400}
          showSelectUnselectAll
          size="small"
        />
      ),
    },
    {
      title: t('TESTGROUP_ADVANCED_DISABLED'),
      dataIndex: 'disabled',
      key: 'disabled',
      responsive: ['md'],
      width: 80,
      align: 'center',
      render: (value, _, index) => (
        <Switch
          checked={value}
          onChange={(checked) => handleConfigChange(index, 'disabled', checked)}
        />
      ),
    },
    {
      title: '',
      key: 'action',
      width: 50,
      render: (_, __, index) => (
        <Button
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveDifficultyConfig(index)}
          size="small"
          danger
          className="rounded-0"
        />
      ),
    },
  ];

  const getTotalQuestions = () =>
    difficultyConfigs.reduce((sum, config) => sum + (config.quantity || 0), 0);

  const getTotalDuration = () =>
    difficultyConfigs.reduce(
      (sum, config) => sum + (config.quantity || 0) * (config.duration || 0),
      0
    );

  return (
    <div>
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          displayName: preset.isNew ? '' : getTranslatedValue(preset.displayName, langKey) || '',
        }}
      >
        <Form.Item
          name="displayName"
          label={t('TESTGROUP_ADVANCED_PRESET_NAME')}
          rules={[
            {
              required: true,
              message: t('TESTGROUP_ADVANCED_PRESET_NAME_REQUIRED'),
            },
          ]}
        >
          <Input placeholder={t('TESTGROUP_ADVANCED_PRESET_NAME_PLACEHOLDER')} />
        </Form.Item>

        {/* Translations section */}
        <div className="mt-3">
          <Button
            type="link"
            onClick={() => setExpandedLanguages(!expandedLanguages)}
            className="p-0 mb-2"
          >
            {expandedLanguages ? t('HIDE_TRANSLATIONS') : t('SHOW_TRANSLATIONS')}
          </Button>

          {expandedLanguages && (
            <div className="border rounded p-3 bg-light">
              <p className="text-muted small mb-3">{t('PRESET_TRANSLATIONS_HELP')}</p>
              {languagesList
                .filter((lang) => lang.value !== langKey)
                .map((lang) => (
                  <Form.Item
                    key={lang.value}
                    name={`displayName_${lang.value}`}
                    label={`${t('TESTGROUP_ADVANCED_PRESET_NAME')} (${t(lang.label)})`}
                    className="mb-2"
                  >
                    <Input
                      placeholder={t('TESTGROUP_ADVANCED_PRESET_NAME_PLACEHOLDER')}
                      size="small"
                    />
                  </Form.Item>
                ))}
            </div>
          )}
        </div>
      </Form>

      <div className="mt-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="m-0 font-weight-500">{t('TESTGROUP_ADVANCED_DIFFICULTY_CONFIGS')}</h4>
          <Button
            type="dashed"
            onClick={handleAddDifficultyConfig}
            icon={<PlusOutlined />}
            block={false}
            className="rounded-0"
          >
            {t('TESTGROUP_ADVANCED_ADD_CONFIG')}
          </Button>
        </div>

        {!isMobile ? (
          <div className="overflow-auto">
            <Table
              columns={columns}
              dataSource={difficultyConfigs}
              pagination={false}
              rowKey={(_, index) => index}
              locale={{
                emptyText: t('TESTGROUP_ADVANCED_NO_CONFIGS_YET'),
              }}
              className="border rounded"
              scroll={{ x: 800 }}
              size="small"
              sticky
            />
          </div>
        ) : (
          <div className="mt-3">
            {difficultyConfigs.length === 0 ? (
              <div className="text-center py-4 border border-dashed rounded">
                <p className="text-muted mb-0">{t('TESTGROUP_ADVANCED_NO_CONFIGS_YET')}</p>
              </div>
            ) : (
              difficultyConfigs.map((config, index) => (
                <Card key={config.difficultyId} className="mb-3" size="small">
                  <Row gutter={[8, 8]}>
                    <Col span={24}>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <strong className="d-flex align-items-center">
                          <span
                            className="badge bg-primary rounded-circle mr-2 d-flex align-items-center justify-content-center"
                            style={{ width: '24px', height: '24px' }}
                          >
                            {index + 1}
                          </span>
                          {t('TESTGROUP_ADVANCED_CONFIG_N').replace('{{n}}', index + 1)}
                        </strong>
                        <Button
                          icon={<DeleteOutlined />}
                          onClick={() => handleRemoveDifficultyConfig(index)}
                          size="small"
                          danger
                          className="rounded-0"
                        />
                      </div>
                    </Col>
                    <Col span={24}>
                      <label className="d-block text-muted mb-1">
                        {t('TESTGROUP_ADVANCED_DIFFICULTY')}
                      </label>
                      <Select
                        value={config.difficultyId}
                        onChange={(val) => handleConfigChange(index, 'difficultyId', val)}
                        style={{ width: '100%' }}
                        placeholder={t('TESTGROUP_ADVANCED_SELECT_DIFFICULTY')}
                      >
                        {testDifficulty
                          .sort((a, b) => a.order - b.order)
                          .map((difficulty) => (
                            <Option key={difficulty.id} value={difficulty.id}>
                              {t(`CREATE_TEST_GROUP_QUESTION_${difficulty.name}`)}
                            </Option>
                          ))}
                      </Select>
                    </Col>
                    {withThemes && (
                      <Col span={24}>
                        <label className="d-block text-muted mb-1">
                          {t('TESTGROUP_ADVANCED_THEME')}
                        </label>
                        <Select
                          value={config.theme}
                          onChange={(val) => handleConfigChange(index, 'theme', val)}
                          style={{ width: '100%' }}
                          placeholder={t('TESTGROUP_ADVANCED_SELECT_THEME')}
                        >
                          {availableThemes.map((theme) => (
                            <Option key={theme} value={theme}>
                              {theme}
                            </Option>
                          ))}
                        </Select>
                      </Col>
                    )}
                    <Col span={12}>
                      <label className="d-block text-muted mb-1">
                        {t('TESTGROUP_ADVANCED_QUANTITY')}
                      </label>
                      <InputNumber
                        value={config.quantity}
                        onChange={(val) => handleConfigChange(index, 'quantity', val)}
                        min={1}
                        max={config.maxQuantity || 1}
                        style={{ width: '100%' }}
                      />
                    </Col>
                    <Col span={12}>
                      <label className="d-block text-muted mb-1">
                        {t('TESTGROUP_ADVANCED_AVAILABLE_QUESTIONS')}
                      </label>
                      <Tag
                        color={config.maxQuantity > 0 ? 'green' : 'red'}
                        className="w-100 text-center"
                      >
                        {config.maxQuantity || 0} {t('AVAILABLE')}
                      </Tag>
                    </Col>
                    <Col span={24}>
                      <label className="d-block text-muted mb-1">
                        {t('TESTGROUP_ADVANCED_DURATION_MIN')}
                      </label>
                      <InputNumber
                        value={config.duration}
                        onChange={(val) => handleConfigChange(index, 'duration', val)}
                        min={1}
                        max={60}
                        style={{ width: '100%' }}
                      />
                    </Col>
                    <Col span={24}>
                      <label className="d-block text-muted mb-1">{t('SUBSKILLS')}</label>
                      <TreeMultiSelect
                        options={availableSubskills}
                        value={config.subSkillIds || []}
                        onChange={(selectedIds) =>
                          handleConfigChange(index, 'subSkillIds', selectedIds)
                        }
                        label={t('SELECT_SUBSKILLS')}
                        labelName="name"
                        width="100%"
                        dropdownWidth={400}
                        showSelectUnselectAll
                        size="small"
                      />
                    </Col>
                    <Col span={24}>
                      <Space>
                        <Switch
                          checked={config.listeningTest}
                          onChange={(checked) =>
                            handleConfigChange(index, 'listeningTest', checked)
                          }
                        />
                        <span>{t('TESTGROUP_ADVANCED_LISTENING')}</span>
                      </Space>
                    </Col>
                    <Col span={24}>
                      <Space>
                        <Switch
                          checked={config.disabled}
                          onChange={(checked) => handleConfigChange(index, 'disabled', checked)}
                        />
                        <span>{t('TESTGROUP_ADVANCED_DISABLED')}</span>
                      </Space>
                    </Col>
                  </Row>
                </Card>
              ))
            )}
          </div>
        )}

        <div className="mt-3 p-3 bg-light rounded text-center">
          <div className="d-flex flex-wrap justify-content-center gap-2">
            <Tag color="blue" className="m-1">
              {t('TESTGROUP_ADVANCED_TOTAL_QUESTIONS')}: <strong>{getTotalQuestions()}</strong>
            </Tag>
            <Tag color="green" className="m-1">
              {t('TESTGROUP_ADVANCED_TOTAL_DURATION')}: <strong>{getTotalDuration()} min</strong>
            </Tag>
          </div>
        </div>
      </div>

      <div className="d-flex justify-content-end mt-4 pt-3 border-top">
        <div className="d-flex flex-wrap gap-2">
          <Button onClick={onCancel} className="rounded-0">
            {t('CANCEL')}
          </Button>
          <Button type="primary" onClick={handleSubmit} className="rounded-0">
            {t('SAVE')}
          </Button>
        </div>
      </div>
    </div>
  );
};
